<template>
  <div class="mx-auto my-2">
    <div v-if="!showTable" class="import-entry-shell">
      <v-card class="import-entry-card" flat>
        <div class="import-entry-card__header">
          <div>
            <div class="import-entry-card__eyebrow">Import Helper</div>
            <div class="import-entry-card__title">Post Basic Import</div>
            <div class="import-entry-card__subtitle">
              Download the sample first, keep the same columns, then upload the
              completed file for validation.
            </div>
          </div>
          <div class="import-entry-card__actions">
            <v-btn
 v-if="$can('post-basic', 'downloadTemplate')"              :disabled="lookupLoading"
              color="primary"
              depressed
              rounded
              @click="downloadStructuredTemplate"
            >
              <v-icon left>mdi-file-download-outline</v-icon>
              <span>Download Sample</span>
            </v-btn>
            <v-btn color="primary" outlined rounded @click="goBack()">
              <v-icon left>mdi-arrow-left</v-icon>
              <span>Back to Records</span>
            </v-btn>
          </div>
        </div>
        <div class="import-entry-card__steps">
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">1</span>
            <span>Download the official post-basic sample template.</span>
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">2</span>
            <span
              >Fill the spreadsheet without renaming or removing the
              headers.</span
            >
          </div>
          <div class="import-entry-card__step">
            <span class="import-entry-card__step-number">3</span>
            <span
              >Upload the file and review the validation summary before
              import.</span
            >
          </div>
        </div>

        <div v-if="!showUpload" class="import-entry-card__uploader">
          <upload-excel-component
            :before-upload="beforeUpload"
            :on-success="handleSuccess"
          />
        </div>
      </v-card>
    </div>

    <graduate-import-review
      v-if="showUpload"
      :file-name="fileName"
      :loading="loading"
      :row-count="tableData.length"
      action-label="Import Post Basic Rows"
      title="Post Basic spreadsheet validated"
      @cancel="goBack"
      @replace="replaceImportFile"
      @upload="uploadGraduates"
    />

    <v-card v-if="showTable" class="mx-2 preservice-list-card" flat>
      <div class="filter-toolbar">
        <div class="filter-toolbar__header">
          <div>
            <div class="filter-toolbar__eyebrow">Pre-service Filters</div>
            <div class="filter-toolbar__title">Post Basic</div>
          </div>
          <div class="filter-toolbar__actions">
            <v-btn
              class="filter-toolbar__clear-button"
              color="primary"
              outlined
              rounded
              @click="clearFilters"
            >
              <v-icon left>mdi-filter-remove-outline</v-icon>
              <span>Clear Filters</span>
            </v-btn>
            <v-btn
              v-if="$can('post-basic', 'import', (canCreatePBasic))"
              class="filter-toolbar__secondary-button"
              color="primary"
              depressed
              rounded
              @click="newImport"
            >
              <v-icon left>mdi-import</v-icon>
              <span>New Import</span>
            </v-btn>
            <v-btn
              v-if="canCreatePBasic"
              class="filter-toolbar__primary-button"
              color="success"
              depressed
              rounded
              @click="addPostBasic"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              <span>Add</span>
            </v-btn>
          </div>
        </div>
        <v-row class="filter-toolbar__fields" dense>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              v-model="name"
              class="filter-toolbar__field"
              dense
              hide-details
              label="Search by Name"
              outlined
              prepend-inner-icon="mdi-account-search-outline"
              rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-text-field
              v-model="idValue"
              class="filter-toolbar__field"
              dense
              hide-details
              label="National ID (FAN)"
              outlined
              prepend-inner-icon="mdi-card-account-details-outline"
              rounded
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-autocomplete
              v-model="programValue"
              :clearable="clearable"
              :items="EDUCATIONAL_PROGRAMS"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              label="Program Education Type"
              outlined
              prepend-inner-icon="mdi-shape-outline"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
              v-model="disciplineValue"
              :clearable="clearable"
              :items="DISCIPLINES"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              label="Department / Discipline"
              outlined
              prepend-inner-icon="mdi-stethoscope"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6" md="6" lg="2">
            <v-autocomplete
              v-model="levelOfStudyValue"
              :clearable="clearable"
              :items="LEVEL_OF_STUDIES"
              :loading="lookupLoading"
              class="filter-toolbar__field"
              dense
              item-text="name"
              item-value="id"
              label="Level Of Study"
              outlined
              prepend-inner-icon="mdi-school-outline"
              return-object
              rounded
            ></v-autocomplete>
          </v-col>
        </v-row>
      </div>
      <v-data-table
        :headers="headers"
        :items="tableData.rows"
        :loading="loading"
        :options.sync="options"
        :server-items-length="tableData.count"
        class="elevation-1"
        loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          {{ item.fullName ? item.fullName : "-" }}
        </template>
        <template v-slot:[`item.sex`]="{ item }">
          {{ item.sex ? item.sex : "-" }}
        </template>
        <template v-slot:[`item.idNo`]="{ item }">
          {{ item.idNo ? formatFanId(item.idNo) : "-" }}
        </template>
        <template v-slot:[`item.backgroundProfession`]="{ item }">
          {{ item.backgroundProfession ? item.backgroundProfession : "-" }}
        </template>
        <template v-slot:[`item.entryYear`]="{ item }">
          {{ item.entryYear ? item.entryYear : "-" }}
        </template>
        <template v-slot:[`item.levelOfEducation`]="{ item }">
          {{ item.levelOfEducation ? item.levelOfEducation : "-" }}
        </template>
        <template v-slot:[`item.expectedYearOfGraduation`]="{ item }">
          {{
            item.expectedYearOfGraduation ? item.expectedYearOfGraduation : "-"
          }}
        </template>
        <template v-slot:[`item.higherLearningInstitution`]="{ item }">
          {{
            item.higherLearningInstitution
              ? item.higherLearningInstitution
              : "-"
          }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                class="preservice-record-action preservice-record-action--view mr-2"
                @click="viewImportedGraduates(item)"
                v-on="on"
                >mdi-eye
              </v-icon>
            </template>
            <span>View post basic detail</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-if="canEditPBasic" v-slot:activator="{ on }">
              <v-icon
                class="preservice-record-action preservice-record-action--edit mr-2"
                @click="editPostBasic(item)"
                v-on="on"
                >mdi-circle-edit-outline
              </v-icon>
            </template>
            <span>Edit Post Basic</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">mdi-account-details-outline</v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                Review identity, education, institution, and grade information.
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              aria-label="Close student detail dialog"
              class="preservice-dialog-close"
              icon
              @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <PostBasicDetail
              v-if="detailDialog"
              :graduates="graduates"
              @close="detailDialog = false"
            ></PostBasicDetail>
          </div>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="addDialog" persistent width="60%">
        <v-card class="preservice-dialog-card">
          <v-card-title class="preservice-dialog-header">
            <div class="preservice-dialog-header__icon">
              <v-icon color="white">
                {{ graduates ? "mdi-account-edit-outline" : "mdi-account-plus-outline" }}
              </v-icon>
            </div>
            <div class="preservice-dialog-header__content">
              <div class="preservice-dialog-header__title">{{ title }}</div>
              <div class="preservice-dialog-header__subtitle">
                {{
                  graduates
                    ? "Update this student's pre-service record."
                    : "Create a new pre-service student record."
                }}
              </div>
            </div>
            <v-spacer></v-spacer>
            <v-btn
              aria-label="Close create or edit student dialog"
              class="preservice-dialog-close"
              icon
              @click="closeDialog()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <div class="preservice-dialog-body">
            <HrdAddPostBasic
              v-if="addDialog"
              :graduates="graduates"
              :submitting="saving"
              @close="addDialog = false"
              @registerOrUpdatePostBasic="registerOrUpdatePostBasic"
            ></HrdAddPostBasic>
          </div>
        </v-card>
      </v-dialog>
    </template>
    <importErrorModal
      :dialogState="importErrorsDialog"
      :importErrors="importErrors"
      @dialogEvent="() => (importErrorsDialog = false)"
    ></importErrorModal>
  </div>
</template>

<script>
import { GraduateService } from "../graduates.service";
import UploadExcelComponent from "../UploadExcel.vue";
import PostBasicDetail from "../PostBasic/post-basic-detail.vue";
import HrdAddPostBasic from "./add-post-basic.vue";
import {
  getDisciplines,
  getEducationalPrograms,
  getEthnicities,
  getHealthProfessions,
  getLevelOfStudies,
  getRegionList,
  getStudentStatuses,
  getUser,
  getUniversities,
  getWoredaList,
  getZoneList,
  getAssignedUniversityOption,
  isUniversityScopedPreserviceUser,
} from "@/utils/helper";
import importErrorModal from "../importErrorModal.vue";
import GraduateImportReview from "@/components/Graduates/GraduateImportReview.vue";
import {
  buildDetailedStructuredTemplateOptions,
  DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS,
  PRESERVICE_STATUS_SHEET_NAME,
  validatePreserviceImportSource,
  validateStructuredPreServiceImport,
} from "@/components/Graduates/graduate-import-utils";
import { downloadStructuredTemplateWorkbook } from "@/components/Graduates/structured-template-workbook";
import { formatFanId, normalizeFanId } from "@/utils/fan-id";
import { YearOfEducEnum } from "@/utils/enums";

export default {
  components: {
    UploadExcelComponent,
    PostBasicDetail,
    HrdAddPostBasic,
    importErrorModal,
    GraduateImportReview,
  },
  data() {
    return {
      fileName: "",
      showTable: true,
      showUpload: false,
      showUploadExcel: false,
      loading: false,
      saving: false,
      importErrorsDialog: false,
      importErrors: [],
      headers: [
        { text: "Full Name", value: "fullName" },
        { text: "Sex", value: "sex" },
        { text: "National ID (FAN)", value: "idNo" },
        { text: "Background Profession", value: "backgroundProfession" },
        { text: "Year of Entry", value: "entryYear" },
        { text: "Level Of Study", value: "levelOfStudy.name" },
        {
          text: "Expected Graduation Year",
          value: "expectedYear",
        },
        {
          text: "Higher Learning Institution",
          value: "university.name",
        },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      tableData: [],
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      addDialog: false,

      lookupLoading: false,
      LEVEL_OF_STUDIES: [],
      EDUCATIONAL_PROGRAMS: [],
      EDUC_LEVEL: [],
      BACKGROUND_PROFESSIONS: [],
      STUDENT_STATUSES: [],
      YEAR_OF_EDUCATION: [],
      DISCIPLINES: [],
      allDisciplines: [],
      ETHNICITIES: [],
      BIRTH_REGIONS: [],
      BIRTH_ZONES: [],
      BIRTH_WOREDAS: [],
      UNIVERSITIES: [],

      programValue: null,
      disciplineValue: null,
      departmentValue: null,
      levelOfStudyValue: null,
      idValue: null,
      name: null,
      clearable: true,
    };
  },
  async beforeMount() {
    this.fetchImportedGraduates();
    this.loadLookups();
  },
  watch: {
    options: {
      handler() {
        this.fetchImportedGraduates();
      },
      deep: true,
    },
    name: function (val) {
      this.name = val;
      this.fetchImportedGraduates();
    },
    idValue: function (val) {
      this.idValue = formatFanId(val);
      this.fetchImportedGraduates();
    },
    programValue: function (val) {
      this.programValue = val == "" ? null : val;
      this.fetchOnFilters();
      this.fetchImportedGraduates();
    },
    disciplineValue: function (val) {
      this.disciplineValue = val == "" ? null : val;
      this.departmentValue = val?.name || null;
      this.fetchImportedGraduates();
    },
    levelOfStudyValue: function (val) {
      this.levelOfStudyValue = val == "" ? null : val;
      this.fetchImportedGraduates();
    },
  },
  computed: {
    canCreatePBasic() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "Pre-service" &&
          permission.canCreate,
      );
    },
    canEditPBasic() {
      return getUser().permissions.some(
        (permission) =>
          permission.menuItem.menuItemName == "Pre-service" &&
          permission.canUpdate,
      );
    },
  },
  methods: {
    formatFanId,
    fetchOnFilters() {
      if (!this.programValue?.id) {
        this.DISCIPLINES = this.allDisciplines;
        return;
      }
      this.DISCIPLINES = this.allDisciplines.filter(
        (item) => item?.educationProgramId == this.programValue.id,
      );
      if (
        this.disciplineValue &&
        !this.DISCIPLINES.some((item) => item?.id === this.disciplineValue.id)
      ) {
        this.disciplineValue = null;
        this.departmentValue = null;
      }
    },
    clearFilters() {
      this.name = null;
      this.idValue = null;
      this.programValue = null;
      this.disciplineValue = null;
      this.levelOfStudyValue = null;
      this.DISCIPLINES = this.allDisciplines;
      this.options.page = 1;
      this.fetchImportedGraduates();
    },
    async fetchImportedGraduates() {
      this.loading = true;
      const { page, itemsPerPage } = this.options;

      let searchParams = "&preServiceLevel=PostBasic";
      this.name !== null && (searchParams += "&name=" + this.name);
      this.idValue !== null &&
        (searchParams += "&idNo=" + normalizeFanId(this.idValue));
      this.programValue !== null &&
        (searchParams += "&educationProgramId=" + this.programValue.id);
      this.departmentValue !== null &&
        (searchParams += "&department=" + this.departmentValue);
      this.levelOfStudyValue !== null &&
        (searchParams += "&levelOfStudyId=" + this.levelOfStudyValue.id);

      let res = await GraduateService.getPostServices(
        page,
        itemsPerPage,
        searchParams,
      );
      if (res.status === 200) {
        this.loading = false;
        this.showTable = true;
        return (this.tableData = res.data);
      }
    },
    async loadLookups() {
      this.lookupLoading = true;
      try {
        this.LEVEL_OF_STUDIES = await getLevelOfStudies();
        this.LEVEL_OF_STUDIES = this.LEVEL_OF_STUDIES.rows.filter(
          (x) => x?.level_type == "Post Basic",
        );
        this.EDUCATIONAL_PROGRAMS = await getEducationalPrograms({
          scope: "standard",
        });
        this.EDUCATIONAL_PROGRAMS = this.EDUCATIONAL_PROGRAMS.rows;
        const disciplines = await getDisciplines({
          scope: "standard",
          levelType: "Post Basic",
        });
        this.allDisciplines = disciplines?.rows || [];
        this.DISCIPLINES = this.allDisciplines;
        this.ETHNICITIES = (await getEthnicities())?.rows || [];
        this.BIRTH_REGIONS = (await getRegionList()) || [];
        this.BIRTH_ZONES = (await getZoneList()) || [];
        this.BIRTH_WOREDAS = (await getWoredaList()) || [];
        const healthProfessions = await getHealthProfessions();
        this.BACKGROUND_PROFESSIONS = (healthProfessions?.rows || []).filter(
          (data) => data?.isActive === true,
        );
        const studentStatuses = await getStudentStatuses();
        this.STUDENT_STATUSES = studentStatuses?.rows || studentStatuses || [];
        this.YEAR_OF_EDUCATION = [{ name: "Not Applicable", code: "NA" }].concat(
          YearOfEducEnum,
        );
        const universityResponse = await getUniversities();
        const activeUniversities = (universityResponse?.rows || []).filter(
          (item) => item?.isActive !== false,
        );
        const assignedUniversity = getAssignedUniversityOption();
        this.UNIVERSITIES =
          isUniversityScopedPreserviceUser() && assignedUniversity
            ? [assignedUniversity]
            : activeUniversities;
      } catch (error) {
        console.error(error);
        this.$toast.error("Unable to load the Post Basic form lookups. Please retry.");
      } finally {
        this.lookupLoading = false;
      }
    },
    buildWorkbookTemplateOptions() {
      return buildDetailedStructuredTemplateOptions({
        lookups: {
          backgroundProfessions: this.BACKGROUND_PROFESSIONS,
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });
    },
    async downloadStructuredTemplate() {
      const templateOptions = this.buildWorkbookTemplateOptions();
      if (
        !Object.values(templateOptions.missingRequirements || {}).every(Boolean)
      ) {
        this.$toast.error(
          "Unable to generate the Post Basic template because one or more lookups are missing.",
        );
        return;
      }

      await downloadStructuredTemplateWorkbook({
        categoryLabel: "Post Basic",
        fileName: "Post_Basic_Sample.xlsx",
        requiresGpa: true,
        templateOptions,
      });
      this.$toast.success("Post_Basic_Sample.xlsx downloaded.");
    },
    newImport() {
      this.showTable = false;
    },
    goBack() {
      this.showTable = true;
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
      this.fetchImportedGraduates();
    },
    replaceImportFile() {
      this.showUpload = false;
      this.tableData = [];
      this.fileName = "";
      this.importErrors = [];
      this.importErrorsDialog = false;
    },
    async uploadGraduates() {
      if (this.tableData.length === 0) {
        this.$toast.error("No file detected, Please provide one!");
        return;
      }
      this.loading = true;
      try {
        let res = await GraduateService.importPostBasic(this.tableData);
        if (res.status === 200) {
          this.showUpload = false;
          switch (res.data.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.fileName = "";
              this.fetchImportedGraduates();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
          }
        } else {
          this.showUpload = false;
          this.fetchImportedGraduates();
          this.$toast.error(
            "There was a problem uploading file, Please try Again!",
          );
        }
      } finally {
        this.loading = false;
      }
    },
    closeDialog() {
      this.addDialog = false;
      this.detailDialog = false;
      this.fetchImportedGraduates();
    },
    beforeUpload(file) {
      const isLt1M = file.size / 1024 / 1024 < 1;
      if (isLt1M) {
        return true;
      }
      this.$toast.warning("Please do not upload files larger than 1m in size.");
      return false;
    },
    handleSuccess({ header, results, fileName, sheets }) {
      const sourceValidation = validatePreserviceImportSource({
        fileName,
        headers: header,
        sheets,
        expectedHeaders: DETAILED_STRUCTURED_TEMPLATE_HEADERS,
        expectedLegacyHeaders: LEGACY_DETAILED_STRUCTURED_TEMPLATE_HEADERS,
        expectedTemplateSheetName: "Post Basic Template",
      });
      if (sourceValidation.hasErrors) {
        this.importErrors = sourceValidation.errors;
        this.importErrorsDialog = true;
        return;
      }

      const validation = validateStructuredPreServiceImport({
        rows: results,
        statusRows: sourceValidation.usesSeparateStatusSheet
          ? sheets?.[PRESERVICE_STATUS_SHEET_NAME]?.results || []
          : null,
        preServiceLevel: "PostBasic",
        lookups: {
          backgroundProfessions: this.BACKGROUND_PROFESSIONS,
          disciplines: this.allDisciplines,
          educationalPrograms: this.EDUCATIONAL_PROGRAMS,
          ethnicities: this.ETHNICITIES,
          birthRegions: this.BIRTH_REGIONS,
          birthZones: this.BIRTH_ZONES,
          birthWoredas: this.BIRTH_WOREDAS,
          levelOfStudies: this.LEVEL_OF_STUDIES,
          studentStatuses: this.STUDENT_STATUSES,
          yearOfEducation: this.YEAR_OF_EDUCATION,
          universities: this.UNIVERSITIES,
        },
      });

      if (validation.hasErrors) {
        this.importErrors = validation.errors;
        this.importErrorsDialog = true;
        return;
      }
      this.importErrors = [];
      this.importErrorsDialog = false;
      this.showUpload = true;
      this.tableData = validation.validRows;
      this.fileName = fileName;
      this.$toast.success(
        `${validation.validRows.length} rows are ready to import.`,
      );
    },
    viewImportedGraduates(item) {
      this.graduates = item;
      this.detailDialog = true;
      this.title = "Post Basic Detail";
    },
    addPostBasic() {
      this.graduates = null;
      this.title = "Add Post Basic";
      this.addDialog = true;
    },
    editPostBasic(item) {
      this.graduates = item;
      this.title = "Edit Post Basic";
      this.addDialog = true;
    },
    async registerOrUpdatePostBasic(graduates) {
      if (this.saving) return;
      this.saving = true;

      try {
        const res = graduates.editMode
          ? await GraduateService.updatePreService(graduates.id, graduates)
          : await GraduateService.createPreService(graduates);

        if (res?.data?.status !== 200) {
          this.$toast.error(
            res?.data?.message ||
              `Unable to ${graduates.editMode ? "update" : "create"} post basic, please try again`,
          );
          return;
        }

        this.$toast.success(res.data.message);
        this.addDialog = false;
        this.fetchImportedGraduates().catch((error) => {
          console.error(error);
          this.loading = false;
          this.$toast.error(
            "The entry was saved, but the list could not be refreshed. Please reload the page.",
          );
        });
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.message ||
            `Unable to ${graduates.editMode ? "update" : "create"} post basic, please try again`,
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.selected {
  line-height: 16px;
  margin: 0 auto;
  font-size: 24px;
  border-radius: 5px;
  text-align: center;
  color: #bbb;
  position: relative;
}

.preservice-list-card {
  border-radius: 24px;
}

.filter-toolbar {
  margin-bottom: 16px;
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(135deg, #f7fbff 0%, #eef6ff 52%, #ffffff 100%);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.filter-toolbar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.filter-toolbar__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.15;
  color: #0f172a;
}

.filter-toolbar__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-toolbar__fields {
  margin: 0 !important;
}

.filter-toolbar__field {
  background: rgba(255, 255, 255, 0.9);
}

.filter-toolbar__primary-button,
.filter-toolbar__secondary-button,
.filter-toolbar__clear-button {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.import-entry-shell {
  margin: 0 8px 24px;
}

.import-entry-card {
  padding: 20px;
  border: 1px solid #dbe7f3;
  border-radius: 24px;
  background: linear-gradient(145deg, #f8fbff 0%, #eef5ff 48%, #ffffff 100%);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.08);
}

.import-entry-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.import-entry-card__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2563eb;
}

.import-entry-card__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.import-entry-card__subtitle {
  margin-top: 8px;
  max-width: 640px;
  color: #475569;
}

.import-entry-card__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.import-entry-card__steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.import-entry-card__step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  color: #334155;
}

.import-entry-card__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-weight: 700;
}

.import-entry-card__uploader {
  margin-top: 20px;
}
</style>
